<template>
  <div ref="streamingFrame" class="streaming-view">
    <!-- CANVAS to draw reaction particles into -->
    <canvas id="canvas" ref="canvas" @mousedown="hideReactionBox()" />
    <!-- Video Player -->
    <player />
    <!-- REACTIONS (Visible only in STREAM mode) -->
    <div
      v-if="isReactionsFeatureOn"
      class="reactions-area"
      @mousedown="showReactionBox()" 
      @mouseenter="showReactionBox()" 
      @mouseleave="hideReactionBox()"
    >
      <div class="background" />
      <reactions-box
        :class="[isReactionBoxVisible ? 'animate-in' : 'animate-out']"
        @send-reaction="sendReaction"
      />
    </div>
  </div>
</template> 

<script>
import { Particle } from '@/classes/Particle';
import Player from '@/components/player';
import ReactionsBox from '@/components/ReactionsBox.vue';
import { mapGetters, } from 'vuex';

import { REACTIONS_FEATURE_FLAG } from '../constants';
import { ReactionsService } from '../services/reactionsService';

export default {
  name: 'StreamingView',
  components: {Player, ReactionsBox},
  data: () => {
    return {
      // Service to send and receive reactions
      reactionsService: null,
      // Flag to manage if Reactions box should be visible
      isReactionBoxVisible: false,
      // Array of all the reaction particles currently on screen
      particles: [],
      // Reference to the Streaming Frame
      frameRef: null,
      // Reference to the Canvas Frame
      canvas: null,
    };
  },

  computed: {
    ...mapGetters(['mode']),
    isReactionsFeatureOn() {
      return REACTIONS_FEATURE_FLAG && this.mode == 'STREAM';
    },
  },
  mounted(){
    if(this.isReactionsFeatureOn){
      // Open WS Connection to Reactions Server
      this.reactionsService = new ReactionsService(this.displayReaction);
      // Start animation loop
      this.animationId = window.requestAnimationFrame(
        this.drawAndAnimate
      );
    }
  },
  unmounted(){
    if(this.isReactionsFeatureOn){
      // Close the websocket connection to reaction server
      this.reactionsService.close();
      
      // Destroy animation loop
      window.cancelAnimationFrame(this.animationId);
      this.animationId = undefined;
    }
  },

  methods: {
    showReactionBox() {
      this.isReactionBoxVisible = true;
    },
    hideReactionBox() {
      this.isReactionBoxVisible = false;
    },

    /*
     *  Recursive function that keeps animating the
     *  Particles on screen 
     */
    drawAndAnimate() {

      if(!this.frameRef){
        this.frameRef = this.$refs['streamingFrame'];
      }

      if(!this.canvas) {
        this.canvas = this.$refs['canvas'];
      }

      const w = this.frameRef.clientWidth;
      const h = this.frameRef.clientHeight;

      // Responsive Canvas width and height properties
      this.canvas.width = w;
      this.canvas.height = h - 40;
      
      // Get Context to draw on
      const ctx = this.canvas.getContext('2d');
    
      // Clear preceding scene
      ctx.clearRect(0, 0, w, h);

      // Animate every Particle
      for (let j = 0; j < this.particles.length; j++) {
        let p = this.particles[j];

        // Move Particle for the next step
        p.moveAhead(h);

        // if particles is outscreen remove it
        if (p.y < 0) {
          this.particles.splice(j, 1);
          j--;
        }

        // Save and draw context
        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.setTransform(p.scale, 0, 0, p.scale, p.x, p.y);
        ctx.drawImage(p.image, 0, 0);
        ctx.restore();

        // After moving update particle angle
        p.angle += 10;
      }
      
      // Loop to the next animation frame
      requestAnimationFrame(this.drawAndAnimate);
    },

    /*
     * Send a Reaction to the web socket server 
     */
    sendReaction(emojiId) {
      this.reactionsService.send(emojiId);
    },

    /*
     * Display a reaction received to the screen 
     */
    displayReaction(emojiId) {
      if(document.visibilityState === 'visible'){
        const newParticle = new Particle(this.frameRef.clientWidth, this.frameRef.clientHeight, emojiId);
  
        if(this.particles.length < 60) {
          this.particles.push(newParticle);
        }
      }
    }
  },
};
</script>

<style scoped>
  
  .streaming-view {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  
  #canvas {
    position: absolute;
    top:0;
    left:0;
    z-index: 2;
  }

  .reactions-area {
    position: absolute;
    bottom: 0;    
    left: 50%;
    transform: translate(-50%, 0);
    width: 50vw;
    height: 10rem;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .background{
    position: absolute;
    bottom: -6rem;
    width: 120%;
    max-width: 40rem;
    height: 90%;
    border-radius: 300px 300px 0 0;
    background: linear-gradient(0deg, rgba(0,0,0,0.6) 20%, rgba(255,255,255,0) 100%);
    filter:blur(20px);
  }

  .animate-in {
    top: 0rem;
    animation: pop-in cubic-bezier(0.175, 0.885, 0.32, 1.275);
    animation-duration: 500ms;
  }
  
  .animate-out {
    position: relative;
    top: 10rem;
    animation: pop-out cubic-bezier(0.175, 0.885, 0.32, 1.275);
    animation-duration: 700ms;
  }
  
  @keyframes pop-in {
    0% {
      position: relative;
      top: 10rem;
    }

    100% {
      position: relative;
      top: 0rem;
    }
  }

  @keyframes pop-out {
    0% {
      position: relative;
      top: 0rem;
    }
    100% {
      position: relative;
      top: 10rem;
    }
  }
</style>
